import { memo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import classNames from 'classnames'

import { Typography } from 'beam/dist/Typography'
import { CircularProgress } from 'beam/dist/Progress'
import useEvaluationFormStyles from './EvaluationForm.styles'
import EvaluationQuestion from './EvaluationQuestion'
import { EVAL_STATE } from './evaluationForm.constants'
import { difference } from '../../../Common/Utils'

const propTypes = {
  sectionTemplate: PropTypes.object,
  sectionIndex: PropTypes.number,
  sectionValues: PropTypes.object,
  isPercentageScoreType: PropTypes.bool,
  evaluationState: PropTypes.number,
  comments: PropTypes.object,
  isSavingValues: PropTypes.bool,
  renderComments: PropTypes.func,
  onAnswerClick: PropTypes.func,
}

const EvaluationSection = ({
  sectionTemplate,
  sectionIndex,
  sectionValues,
  isPercentageScoreType,
  evaluationState,
  comments,
  isSavingValues,
  renderComments,
  onAnswerClick,
}) => {
  const classes = useEvaluationFormStyles()
  const { t } = useTranslation()

  const { id, name, questions, weight } = sectionTemplate

  const hasCannotScoreState = evaluationState === EVAL_STATE.CANNOT_SCORE
  const hasInProgressState = evaluationState === EVAL_STATE.IN_PROGRESS

  return (
    <section key={id} className={classes.section}>
      <header className={classes.sectionHeader}>
        <div className={classes.sectionTitle}>
          <Typography variant="button" noWrap>
            {name}
          </Typography>
          {!hasCannotScoreState && typeof sectionValues.totalScore !== 'undefined' && (
            <div className={classes.sectionScore}>
              <Typography variant="caption">{t('sectionScore')}</Typography>
              <Typography
                variant="body2"
                className={classNames(
                  classes.score,
                  isSavingValues && hasInProgressState && classes.scoreHide,
                )}
                auto-qa-id={`Evaluation-Form-Section-${id}-Score`}
                data-testid={`Evaluation-Form-Section-${id}-Score`}
              >
                {Number.parseFloat(sectionValues.totalScore).toFixed(2)}
                {isPercentageScoreType && '%'}
              </Typography>
              {isSavingValues && hasInProgressState && <CircularProgress className={classes.scoreProgress} size={18} />}
            </div>
          )}
        </div>
        {isPercentageScoreType && (
          <Typography variant="caption" className={classes.sectionWeight}>
            {`Worth ${Number.parseFloat(weight * 100).toFixed(2)}% of evaluation`}
          </Typography>
        )}
      </header>
      {renderComments(
        comments.sectionComments[id] || [],
        `evaluation-form-section-${sectionIndex + 1}`,
        t('addASectionComment'),
        id,
      )}
      {questions.map((question, index) => (
        <EvaluationQuestion
          key={question.id}
          questionTemplate={question}
          questionValues={sectionValues.questions.find(({ id }) => id === question.id)}
          isPercentageScoreType={isPercentageScoreType}
          evaluationState={evaluationState}
          comments={comments.questionComments[question.id] || []}
          sectionIndex={sectionIndex}
          questionIndex={index}
          sectionId={id}
          renderComments={renderComments}
          onAnswerClick={onAnswerClick}
        />
      ))}
    </section>
  )
}

EvaluationSection.propTypes = propTypes

export default memo(EvaluationSection, (prevProps, nextProps) => {
  const diff = difference(nextProps, prevProps)
  return isEmpty(diff)
})
